import "./Profile.css";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUser,
  FaBoxOpen,
  FaLock,
  FaTruck,
  FaReceipt,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaCity,
  FaMapPin,
  FaCheckCircle,
  FaShoppingBag,
  FaShieldAlt,
  FaEye,
  FaEyeSlash,
  FaArrowLeft,
  FaPen,
  FaClock,
  FaWallet,
  FaChevronLeft,
  FaSave,
  FaKey,
} from "react-icons/fa";
import api from "../../services/api";
import { useNotification } from "../../context/NotificationContext";

const tabMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.24, ease: "easeOut" },
};

function Profile() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [activeTab, setActiveTab] = useState("profile");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }

    const loadAccount = async () => {
      try {
        const [profileResponse, ordersResponse] = await Promise.all([
          api.get("/profile", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          api.get("/profile/orders", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setProfile({
          first_name: profileResponse.data.first_name || "",
          last_name: profileResponse.data.last_name || "",
          email: profileResponse.data.email || "",
          phone: profileResponse.data.phone || "",
          address: profileResponse.data.address || "",
          city: profileResponse.data.city || "",
          postal_code: profileResponse.data.postal_code || "",
        });
        setOrders(Array.isArray(ordersResponse.data) ? ordersResponse.data : []);
      } catch (error) {
        if (error?.response?.status === 401) {
          navigate("/login");
          return;
        }
        showNotification("לא הצלחנו לטעון את האזור האישי", "error");
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, [navigate, showNotification]);

  const sortedOrders = useMemo(
    () =>
      [...orders].sort(
        (a, b) => new Date(b.order_date || 0) - new Date(a.order_date || 0),
      ),
    [orders],
  );

  const deliveredOrders = useMemo(
    () => orders.filter((order) => order.status === "Delivered").length,
    [orders],
  );

  const totalSpent = useMemo(
    () =>
      orders
        .filter((order) => order.status !== "Cancelled")
        .reduce((sum, order) => sum + Number(order.total_price || 0), 0),
    [orders],
  );

  const latestOrder = sortedOrders[0];

  const fullName =
    `${profile.first_name} ${profile.last_name}`.trim() || "לקוח Cervica";

  const initials =
    `${profile.first_name?.[0] || ""}${profile.last_name?.[0] || ""}` || "C";

  const handleProfileChange = (event) => {
    setProfile((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", profile, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      showNotification("הפרטים עודכנו בהצלחה", "success");
    } catch (error) {
      showNotification(
        error?.response?.data?.message || "לא הצלחנו לשמור את הפרטים",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const translateStatus = (status) => {
    const statuses = {
      Pending: "התקבלה",
      Paid: "התשלום אושר",
      Preparing: "בהכנה",
      Shipped: "במשלוח",
      Delivered: "נמסרה",
      Cancelled: "בוטלה",
    };

    return statuses[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      Pending: "pending",
      Paid: "paid",
      Preparing: "preparing",
      Shipped: "shipped",
      Delivered: "delivered",
      Cancelled: "cancelled",
    };

    return classes[status] || "pending";
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  return (
    <main className="profile-page">
      <div className="profile-orb profile-orb-one" />
      <div className="profile-orb profile-orb-two" />

      <section className="profile-shell">
        <motion.section
          className="profile-hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <div className="profile-hero-glow" />

          <div className="profile-identity">
            <div className="profile-avatar-wrap">
              <div className="profile-avatar">{initials.toUpperCase()}</div>
              <span className="profile-avatar-check" aria-label="חשבון פעיל">
                <FaCheckCircle />
              </span>
            </div>

            <div className="profile-identity-copy">
              <span className="profile-eyebrow">האזור האישי שלך</span>
              <h1>שלום, {profile.first_name || "לקוח"}</h1>
              <p>
                כל פרטי החשבון, ההזמנות והאבטחה שלך במקום אחד — פשוט, נקי
                ונוח.
              </p>
              <div className="profile-identity-meta">
                {profile.email && (
                  <span>
                    <FaEnvelope /> {profile.email}
                  </span>
                )}
                {profile.phone && (
                  <span>
                    <FaPhoneAlt /> {profile.phone}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="profile-hero-stats">
            <div className="profile-stat">
              <span className="profile-stat-icon">
                <FaShoppingBag />
              </span>
              <div>
                <strong>{orders.length}</strong>
                <span>הזמנות</span>
              </div>
            </div>

            <div className="profile-stat">
              <span className="profile-stat-icon">
                <FaCheckCircle />
              </span>
              <div>
                <strong>{deliveredOrders}</strong>
                <span>נמסרו</span>
              </div>
            </div>

            <div className="profile-stat profile-stat-wide">
              <span className="profile-stat-icon">
                <FaWallet />
              </span>
              <div>
                <strong>₪{totalSpent.toFixed(0)}</strong>
                <span>סה״כ רכישות</span>
              </div>
            </div>
          </div>
        </motion.section>

        {latestOrder && (
          <motion.div
            className="latest-order-strip"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.4 }}
          >
            <div className="latest-order-main">
              <span className="latest-order-icon">
                <FaTruck />
              </span>
              <div>
                <span>ההזמנה האחרונה</span>
                <strong>#{latestOrder.order_id}</strong>
              </div>
              <span
                className={`order-status ${getStatusClass(latestOrder.status)}`}
              >
                {translateStatus(latestOrder.status)}
              </span>
            </div>

            <Link
              className="latest-order-link"
              to={`/track-order?order=${latestOrder.order_id}`}
            >
              מעקב הזמנה <FaChevronLeft />
            </Link>
          </motion.div>
        )}

        <section className="profile-layout">
          <aside className="profile-sidebar">
            <div className="profile-mini-card">
              <div className="profile-mini-avatar">{initials.toUpperCase()}</div>
              <div>
                <strong>{fullName}</strong>
                <span>{profile.email || "חשבון Cervica"}</span>
              </div>
            </div>

            <nav className="profile-menu" aria-label="תפריט אזור אישי">
              <ProfileTabButton
                active={activeTab === "profile"}
                icon={<FaUser />}
                title="פרטים אישיים"
                subtitle="עדכון פרטי החשבון"
                onClick={() => setActiveTab("profile")}
              />
              <ProfileTabButton
                active={activeTab === "orders"}
                icon={<FaBoxOpen />}
                title="ההזמנות שלי"
                subtitle={`${orders.length} הזמנות בחשבון`}
                onClick={() => setActiveTab("orders")}
              />
              <ProfileTabButton
                active={activeTab === "password"}
                icon={<FaShieldAlt />}
                title="אבטחה וסיסמה"
                subtitle="שמירה על החשבון"
                onClick={() => setActiveTab("password")}
              />
            </nav>

            <div className="profile-help-card">
              <span className="profile-help-icon">
                <FaShieldAlt />
              </span>
              <div>
                <strong>הפרטים שלך מוגנים</strong>
                <p>עדכן סיסמה מעת לעת ושמור על פרטי הכניסה שלך.</p>
              </div>
            </div>
          </aside>

          <div className="profile-content">
            <AnimatePresence mode="wait">
              {activeTab === "profile" && (
                <motion.div key="profile" {...tabMotion} className="profile-card">
                  <SectionHeading
                    icon={<FaPen />}
                    eyebrow="החשבון שלי"
                    title="פרטים אישיים"
                    text="עדכן כאן את פרטי הקשר והמשלוח שלך."
                  />

                  <div className="profile-form-grid">
                    <ProfileField
                      icon={<FaUser />}
                      label="שם פרטי"
                      name="first_name"
                      value={profile.first_name}
                      onChange={handleProfileChange}
                      placeholder="השם הפרטי שלך"
                    />
                    <ProfileField
                      icon={<FaUser />}
                      label="שם משפחה"
                      name="last_name"
                      value={profile.last_name}
                      onChange={handleProfileChange}
                      placeholder="שם המשפחה שלך"
                    />
                    <ProfileField
                      icon={<FaEnvelope />}
                      label="אימייל"
                      name="email"
                      type="email"
                      value={profile.email}
                      onChange={handleProfileChange}
                      placeholder="name@example.com"
                    />
                    <ProfileField
                      icon={<FaPhoneAlt />}
                      label="טלפון"
                      name="phone"
                      value={profile.phone}
                      onChange={handleProfileChange}
                      placeholder="05X-XXXXXXX"
                    />
                    <ProfileField
                      icon={<FaMapMarkerAlt />}
                      label="כתובת"
                      name="address"
                      value={profile.address}
                      onChange={handleProfileChange}
                      placeholder="רחוב ומספר בית"
                      wide
                    />
                    <ProfileField
                      icon={<FaCity />}
                      label="עיר"
                      name="city"
                      value={profile.city}
                      onChange={handleProfileChange}
                      placeholder="עיר"
                    />
                    <ProfileField
                      icon={<FaMapPin />}
                      label="מיקוד"
                      name="postal_code"
                      value={profile.postal_code}
                      onChange={handleProfileChange}
                      placeholder="מיקוד"
                    />
                  </div>

                  <div className="profile-form-footer">
                    <div className="profile-save-note">
                      <FaCheckCircle />
                      <span>השינויים נשמרים רק לאחר לחיצה על הכפתור.</span>
                    </div>
                    <button
                      className="profile-primary-btn"
                      onClick={saveProfile}
                      disabled={saving}
                    >
                      <FaSave />
                      {saving ? "שומר..." : "שמירת שינויים"}
                    </button>
                  </div>
                </motion.div>
              )}

              {activeTab === "orders" && (
                <motion.div key="orders" {...tabMotion} className="profile-card">
                  <SectionHeading
                    icon={<FaBoxOpen />}
                    eyebrow="היסטוריית רכישות"
                    title="ההזמנות שלי"
                    text="כל ההזמנות שלך, הסטטוס שלהן וקישורים למעקב ולקבלה."
                  />

                  {orders.length === 0 ? (
                    <div className="profile-empty-state">
                      <span className="profile-empty-icon">
                        <FaShoppingBag />
                      </span>
                      <h3>עדיין אין הזמנות</h3>
                      <p>אחרי הרכישה הראשונה שלך, כל הפרטים יופיעו כאן.</p>
                      <Link to="/product" className="profile-primary-btn">
                        לצפייה במוצרים <FaArrowLeft />
                      </Link>
                    </div>
                  ) : (
                    <div className="orders-list">
                      {sortedOrders.map((order, index) => (
                        <motion.article
                          className="order-card"
                          key={order.order_id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(index * 0.04, 0.24) }}
                        >
                          <div className="order-card-head">
                            <div className="order-number-wrap">
                              <span className="order-icon-box">
                                <FaBoxOpen />
                              </span>
                              <div>
                                <span className="order-label">הזמנה</span>
                                <h3>#{order.order_id}</h3>
                              </div>
                            </div>

                            <span
                              className={`order-status ${getStatusClass(
                                order.status,
                              )}`}
                            >
                              {translateStatus(order.status)}
                            </span>
                          </div>

                          <div className="order-card-details">
                            <div>
                              <span>
                                <FaClock /> תאריך הזמנה
                              </span>
                              <strong>
                                {new Date(order.order_date).toLocaleDateString(
                                  "he-IL",
                                )}
                              </strong>
                            </div>
                            <div>
                              <span>
                                <FaWallet /> סכום
                              </span>
                              <strong>
                                ₪{Number(order.total_price || 0).toFixed(2)}
                              </strong>
                            </div>
                          </div>

                          <div className="profile-order-actions">
                            <Link
                              to={`/track-order?order=${order.order_id}`}
                              className="profile-order-btn"
                            >
                              <FaTruck /> מעקב הזמנה
                            </Link>
                            <Link
                              to={`/receipt?order=${order.order_id}`}
                              className="profile-order-btn secondary"
                            >
                              <FaReceipt /> צפייה בקבלה
                            </Link>
                          </div>
                        </motion.article>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === "password" && (
                <motion.div key="password" {...tabMotion} className="profile-card">
                  <SectionHeading
                    icon={<FaShieldAlt />}
                    eyebrow="אבטחת החשבון"
                    title="שינוי סיסמה"
                    text="בחר סיסמה חדשה וחזקה כדי לשמור על החשבון שלך מוגן."
                  />

                  <div className="security-layout">
                    <PasswordForm />

                    <aside className="security-info-card">
                      <span className="security-shield">
                        <FaShieldAlt />
                      </span>
                      <h3>סיסמה טובה מגנה על החשבון</h3>
                      <ul>
                        <li>
                          <FaCheckCircle /> השתמש בסיסמה שלא השתמשת בה בעבר.
                        </li>
                        <li>
                          <FaCheckCircle /> שלב אותיות, מספרים ותווים מיוחדים.
                        </li>
                        <li>
                          <FaCheckCircle /> אל תשתף את הסיסמה עם אחרים.
                        </li>
                      </ul>
                    </aside>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </section>
    </main>
  );
}

function ProfileTabButton({ active, icon, title, subtitle, onClick }) {
  return (
    <button className={active ? "active" : ""} onClick={onClick} type="button">
      <span className="profile-menu-icon">{icon}</span>
      <span className="profile-menu-copy">
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      <FaChevronLeft className="profile-menu-arrow" />
    </button>
  );
}

function SectionHeading({ icon, eyebrow, title, text }) {
  return (
    <header className="profile-section-heading">
      <span className="profile-section-icon">{icon}</span>
      <div>
        <span className="profile-section-eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
    </header>
  );
}

function ProfileField({ icon, label, wide, ...inputProps }) {
  return (
    <label className={`profile-field ${wide ? "wide" : ""}`}>
      <span className="profile-field-label">{label}</span>
      <div className="profile-input-wrap">
        <span className="profile-input-icon">{icon}</span>
        <input {...inputProps} />
      </div>
    </label>
  );
}

function PasswordForm() {
  const { showNotification } = useNotification();
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
  });

  const changePassword = async () => {
    if (!passwords.currentPassword || !passwords.newPassword) {
      showNotification("יש למלא את שתי הסיסמאות", "warning");
      return;
    }

    setSaving(true);
    try {
      await api.put("/profile/password", passwords, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      showNotification("הסיסמה עודכנה בהצלחה", "success");
      setPasswords({ currentPassword: "", newPassword: "" });
    } catch (error) {
      showNotification(
        error?.response?.data?.message || "לא הצלחנו לעדכן את הסיסמה",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="password-form">
      <label className="profile-field wide">
        <span className="profile-field-label">סיסמה נוכחית</span>
        <div className="profile-input-wrap password-input-wrap">
          <span className="profile-input-icon">
            <FaLock />
          </span>
          <input
            type={showCurrent ? "text" : "password"}
            placeholder="הזן את הסיסמה הנוכחית"
            value={passwords.currentPassword}
            onChange={(event) =>
              setPasswords((current) => ({
                ...current,
                currentPassword: event.target.value,
              }))
            }
          />
          <button
            type="button"
            className="password-visibility-btn"
            onClick={() => setShowCurrent((value) => !value)}
            aria-label={showCurrent ? "הסתר סיסמה" : "הצג סיסמה"}
          >
            {showCurrent ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </label>

      <label className="profile-field wide">
        <span className="profile-field-label">סיסמה חדשה</span>
        <div className="profile-input-wrap password-input-wrap">
          <span className="profile-input-icon">
            <FaKey />
          </span>
          <input
            type={showNew ? "text" : "password"}
            placeholder="בחר סיסמה חדשה"
            value={passwords.newPassword}
            onChange={(event) =>
              setPasswords((current) => ({
                ...current,
                newPassword: event.target.value,
              }))
            }
          />
          <button
            type="button"
            className="password-visibility-btn"
            onClick={() => setShowNew((value) => !value)}
            aria-label={showNew ? "הסתר סיסמה" : "הצג סיסמה"}
          >
            {showNew ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </label>

      <button
        className="profile-primary-btn password-submit"
        type="button"
        onClick={changePassword}
        disabled={saving}
      >
        <FaShieldAlt /> {saving ? "מעדכן..." : "עדכון סיסמה"}
      </button>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <main className="profile-page profile-loading-page">
      <section className="profile-shell">
        <div className="profile-skeleton profile-skeleton-hero" />
        <div className="profile-layout">
          <div className="profile-skeleton profile-skeleton-side" />
          <div className="profile-skeleton profile-skeleton-content" />
        </div>
      </section>
    </main>
  );
}

export default Profile;
